import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Plus, Edit, Trash2, Eye, EyeOff, ChevronLeft, ChevronRight,
  Settings, Upload, AlertTriangle, UserPlus, User, List, X, Phone, Mail,
  Share2, Copy, Link, RotateCcw, ExternalLink, Download, QrCode, Ticket,
  Users, CheckCircle, XCircle, Wine, MessageCircle, RefreshCw
} from "lucide-react";
import { API, BACKEND_URL } from "@/config/api";
import PasswordInput from "@/components/common/PasswordInput";

const BonocopasTab = ({ credentials, events, fetchEvents, isGestor, gestorPrivileges, isSuperuser, setActiveTab }) => {
  const [bonocopasVendidos, setBonocopasVendidos] = useState([]);
  const [bonocopasEventoSeleccionado, setBonocopasEventoSeleccionado] = useState(null);
  const [loadingBonocopas, setLoadingBonocopas] = useState(false);

  const fetchBonocopas = useCallback(async (eventId) => {
    if (!eventId) return;
    setLoadingBonocopas(true);
    try {
      const response = await axios.get(`${API}/admin/events/${eventId}/bonocopas`, {
        headers: { Authorization: `Basic ${credentials}` }
      });
      setBonocopasVendidos(response.data.bonocopas || []);
    } catch (e) {
      console.error("Error fetching bonocopas:", e);
      setBonocopasVendidos([]);
    } finally {
      setLoadingBonocopas(false);
    }
  }, [credentials]);

  return (
    <>
          <>
            <div className="form-section mb-6">
              <h3 className="form-title mb-4 flex items-center gap-2">
                <Wine size={20} className="text-yellow-500" />
                Bonocopas Vendidos
              </h3>
              
              {/* Event Selector */}
              <div className="mb-6">
                <label className="form-label">Selecciona un Evento</label>
                <select
                  className="custom-input"
                  value={bonocopasEventoSeleccionado || ""}
                  onChange={(e) => {
                    const eventId = e.target.value;
                    setBonocopasEventoSeleccionado(eventId);
                    if (eventId) {
                      fetchBonocopas(eventId);
                    } else {
                      setBonocopasVendidos([]);
                    }
                  }}
                  data-testid="bonocopas-event-select"
                >
                  <option value="">-- Selecciona un evento --</option>
                  {events.filter(e => e.bonocopas_enabled).map(event => (
                    <option key={event.id} value={event.id}>{event.nombre}</option>
                  ))}
                </select>
              </div>

              {/* Loading */}
              {loadingBonocopas && (
                <div className="text-center py-8">
                  <span className="spinner"></span>
                </div>
              )}

              {/* Bonocopas List */}
              {bonocopasEventoSeleccionado && !loadingBonocopas && (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <p className="text-[var(--text-muted)]">
                      {bonocopasVendidos.filter(b => b.pagado).length} bonocopas vendidos
                    </p>
                    <a 
                      href="/barra" 
                      target="_blank"
                      className="text-yellow-500 hover:text-yellow-400 text-sm flex items-center gap-1"
                    >
                      <ExternalLink size={14} />
                      Abrir Panel de Barra
                    </a>
                  </div>

                  {bonocopasVendidos.length === 0 ? (
                    <p className="text-[var(--text-muted)] text-center py-8">
                      No hay bonocopas vendidos para este evento.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {bonocopasVendidos.filter(b => b.pagado).map((bonocopa) => (
                        <div 
                          key={bonocopa.id}
                          className={`bg-[var(--surface)] border p-4 ${
                            bonocopa.copas_consumidas >= bonocopa.copas_totales
                              ? 'border-gray-700 opacity-60'
                              : 'border-yellow-500/30'
                          }`}
                        >
                          <div className="flex flex-wrap justify-between items-start gap-4">
                            <div>
                              <p className="text-yellow-500 font-mono font-bold">{bonocopa.codigo}</p>
                              <p className="text-white">{bonocopa.nombre}</p>
                              <p className="text-[var(--text-muted)] text-xs">{bonocopa.telefono} | {bonocopa.email}</p>
                              {bonocopa.tipo_nombre && (
                                <p className="text-[var(--text-muted)] text-xs mt-1">{bonocopa.tipo_nombre}</p>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="flex items-center gap-2">
                                <span className={`text-2xl font-bold ${
                                  bonocopa.copas_consumidas >= bonocopa.copas_totales
                                    ? 'text-gray-500'
                                    : 'text-yellow-500'
                                }`}>
                                  {bonocopa.copas_totales - bonocopa.copas_consumidas}
                                </span>
                                <span className="text-[var(--text-muted)]">/ {bonocopa.copas_totales}</span>
                              </div>
                              <p className="text-xs text-[var(--text-muted)]">copas restantes</p>
                              {bonocopa.copas_consumidas >= bonocopa.copas_totales && (
                                <span className="text-xs text-gray-500 uppercase">Agotado</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {!bonocopasEventoSeleccionado && (
                <p className="text-[var(--text-muted)] text-center py-8">
                  Selecciona un evento para ver los bonocopas vendidos.
                </p>
              )}
            </div>
          </>

    </>
  );
};

export default BonocopasTab;
